import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Checkbox,
	FormControlLabel,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	TextField,
	Box
} from "@mui/material";
import { useState, useMemo } from "react";
import MDButton from "components/Basics/MDButton";
import MDBox from "components/Basics/MDBox";
import { t } from "i18next";
import MDTypography from "components/Basics/MDTypography";

const DictionarySelectionDialog = ({
	open,
	handleClose,
	assistantDictionaries,
	selectedDictionaries,
	onSelectionChange
}) => {
	const [searchQuery, setSearchQuery] = useState("");

	// Filter dictionaries based on search query
	const filteredDictionaries = useMemo(() => {
		if (!assistantDictionaries) return {};

		const filtered = {};
		Object.keys(assistantDictionaries).forEach(key => {
			const dictionary = assistantDictionaries[key];
			const matchesSearch =
				dictionary.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
				dictionary.label?.fr?.toLowerCase().includes(searchQuery.toLowerCase());

			if (matchesSearch) {
				filtered[key] = dictionary;
			}
		});
		return filtered;
	}, [assistantDictionaries, searchQuery]);

	const handleToggleDictionary = (dictionaryKey, dictionary) => {
		const newSelectedDictionaries = { ...selectedDictionaries };

		if (newSelectedDictionaries[dictionaryKey]) {
			delete newSelectedDictionaries[dictionaryKey];
		} else {
			newSelectedDictionaries[dictionaryKey] = dictionary;
		}

		onSelectionChange(newSelectedDictionaries);
	};

	const handleConfirm = () => {
		handleClose();
	};

	const handleCancel = () => {
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby="dictionary-selection-dialog-title"
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					height: "80%"
				}
			}}
		>
			<DialogTitle id="dictionary-selection-dialog-title">Sélection des dictionnaires</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
				<MDBox mb={2}>
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Rechercher par code ou libellé..."
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
						size="small"
					/>
				</MDBox>
				<Box sx={{ flex: 1, overflow: "auto" }}>
					<List>
						{Object.keys(filteredDictionaries).map(dictionaryKey => {
							const dictionary = filteredDictionaries[dictionaryKey];
							const isSelected = Boolean(selectedDictionaries[dictionaryKey]);

							return (
								<ListItem key={dictionaryKey} disablePadding>
									<ListItemButton
										onClick={() => handleToggleDictionary(dictionaryKey, dictionary)}
										dense
									>
										<ListItemIcon>
											<Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple />
										</ListItemIcon>
										<ListItemText
											primary={
												<MDTypography variant="h6">
													{dictionary.label?.fr || dictionary.code || dictionaryKey}
												</MDTypography>
											}
											secondary={
												<MDTypography variant="body2" color="textSecondary" fontSize="small">
													{dictionary.code !== dictionary.label?.fr ? dictionary.code : null}
												</MDTypography>
											}
										/>
									</ListItemButton>
								</ListItem>
							);
						})}
						{Object.keys(filteredDictionaries).length === 0 && (
							<ListItem>
								<ListItemText
									primary="Aucun dictionnaire trouvé"
									sx={{ textAlign: "center", fontStyle: "italic" }}
								/>
							</ListItem>
						)}
					</List>
				</Box>
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={handleCancel}>
					Annuler
				</MDButton>
				<MDButton variant="contained" color="info" onClick={handleConfirm}>
					Confirmer
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default DictionarySelectionDialog;
